import React, { useState } from 'react'
import { AiOutlineInfoCircle, AiOutlineSearch } from "react-icons/ai"
import { Btn, COLORS, Chip, EmptyState, INPUT_STYLE, Kpi, downloadCsv, fmtShort, money, todayIso, addDays, useNarrow } from '../FieldOpsKit'
import { REPS } from '../demoData'
import { buildUnrouted } from '../unrouted'

/**
 * Unrouted stores: Altitude customers with no route in Sage. The portal reads routes from Sage (it never changes
 * them), so this list is for whoever assigns routes there: sorted by what the store bought in the last 90 days.
 */
const PAGE = 25

export default function UnroutedTab({ ctx }) {
    const [list] = useState(buildUnrouted)
    const [search, setSearch] = useState('')
    const [rep, setRep] = useState('all')
    const [filter, setFilter] = useState('all') // all | active | never
    const [page, setPage] = useState(0)
    const narrow = useNarrow(800)
    const cutoff = addDays(todayIso(), -90)

    const q = search.trim().toLowerCase()
    const shown = list.filter((u) => {
        if (rep !== 'all' && u.rep !== rep) return false
        if (filter === 'active' && !(u.lastOrder && u.lastOrder >= cutoff)) return false
        if (filter === 'never' && u.lastOrder) return false
        return q === '' || u.name.toLowerCase().indexOf(q) >= 0 || u.city.toLowerCase().indexOf(q) >= 0
    }).sort((a, b) => b.sales90 - a.sales90)
    const pages = Math.max(1, Math.ceil(shown.length / PAGE))
    const p = Math.min(page, pages - 1)
    const view = shown.slice(p * PAGE, p * PAGE + PAGE)
    const active = list.filter((u) => u.lastOrder && u.lastOrder >= cutoff)

    const exportCsv = () => {
        downloadCsv('unrouted-stores.csv', [['Store', 'City', 'Salesperson', 'Last order', 'Sales last 90 days']].concat(shown.map((u) => [u.name, u.city, u.rep, u.lastOrder || '', u.sales90])))
        ctx.toast('Exported ' + shown.length + ' stores to CSV.')
    }

    return (
        <React.Fragment>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: '#FFFBEB', border: '1px solid #FDE68A', color: '#92400E', borderRadius: 10, padding: '10px 14px', fontSize: '0.82rem', marginBottom: 14 }}>
                <AiOutlineInfoCircle style={{ fontSize: '1.1rem', flexShrink: 0 }} />
                <span>Routes are assigned in Sage (customer field <strong>UDF_RUTA</strong>). Export this list for whoever assigns them; once a store has a route it appears in Sales Routes, Goals and Agenda automatically.</span>
            </div>
            <div className="row g-3 mb-3">
                <div className="col-6 col-lg-3"><Kpi label="Stores without route" value={list.length} sub="Altitude" status="warning" /></div>
                <div className="col-6 col-lg-3"><Kpi label="Bought in 90 days" value={active.length} sub="Priority to assign" status="danger" /></div>
                <div className="col-6 col-lg-3"><Kpi label="Never ordered" value={list.filter((u) => !u.lastOrder).length} sub="Probably inactive" /></div>
                <div className="col-6 col-lg-3"><Kpi label="Sales last 90 days" value={money(list.reduce((a, u) => a + u.sales90, 0))} sub="Not counted in any route" /></div>
            </div>
            <div className="row g-2 mb-3 align-items-center">
                <div className="col-12 col-md-4"><div style={{ position: 'relative' }}>
                    <AiOutlineSearch style={{ position: 'absolute', left: 12, top: 11, color: COLORS.muted }} />
                    <input type="text" placeholder="Search store or city" value={search} onChange={(e) => { setSearch(e.target.value); setPage(0) }} style={{ ...INPUT_STYLE, width: '100%', paddingLeft: 34 }} />
                </div></div>
                <div className="col-6 col-md-2"><select value={rep} onChange={(e) => { setRep(e.target.value); setPage(0) }} style={{ ...INPUT_STYLE, width: '100%' }} aria-label="Salesperson"><option value="all">All reps</option>{REPS.map((r) => <option key={r} value={r}>{r}</option>)}</select></div>
                <div className="col-6 col-md-3"><select value={filter} onChange={(e) => { setFilter(e.target.value); setPage(0) }} style={{ ...INPUT_STYLE, width: '100%' }} aria-label="Activity"><option value="all">All stores</option><option value="active">Bought in last 90 days</option><option value="never">Never ordered</option></select></div>
                <div className="col-12 col-md-3" style={{ textAlign: 'right' }}><Btn kind="secondary" onClick={exportCsv}>Export CSV</Btn></div>
            </div>

            {shown.length === 0 ? <EmptyState title="No stores match" text="Change the search or filters." /> : (
                <React.Fragment>
                    {narrow ? view.map((u) => (
                        <div key={u.id} style={{ background: '#FFFFFF', border: '1px solid ' + COLORS.line, borderRadius: 10, padding: '10px 12px', marginBottom: 8 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}><strong style={{ color: COLORS.navy }}>{u.name}</strong><span style={{ color: COLORS.slate, fontSize: '0.8rem' }}>{u.rep}</span></div>
                            <div style={{ fontSize: '0.75rem', color: COLORS.slate }}>{u.city} · {u.lastOrder ? 'Last order ' + fmtShort(u.lastOrder) : 'Never ordered'} · {money(u.sales90)}</div>
                        </div>
                    )) : (
                        <div className="tableFixHeadSmall"><table className="table table-clean">
                            <thead><tr><th>Store</th><th>City</th><th>Rep</th><th>Last order</th><th>Sales last 90 days</th><th>Priority</th></tr></thead>
                            <tbody>{view.map((u) => (
                                <tr key={u.id}>
                                    <td style={{ fontWeight: 600 }}>{u.name}</td><td>{u.city}</td><td>{u.rep}</td>
                                    <td>{u.lastOrder ? fmtShort(u.lastOrder) : <span style={{ color: COLORS.muted }}>Never</span>}</td>
                                    <td>{money(u.sales90)}</td>
                                    <td>{u.lastOrder && u.lastOrder >= cutoff ? <Chip tone="red">Assign now</Chip> : u.lastOrder ? <Chip tone="amber">Review</Chip> : <Chip tone="gray">Inactive</Chip>}</td>
                                </tr>
                            ))}</tbody>
                        </table></div>
                    )}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 10, fontSize: '0.8rem', color: COLORS.slate }}>
                        <span>{shown.length} stores · page {p + 1} of {pages}</span>
                        <span style={{ display: 'flex', gap: 6 }}><Btn kind="secondary" small disabled={p === 0} onClick={() => setPage(p - 1)}>Previous</Btn><Btn kind="secondary" small disabled={p >= pages - 1} onClick={() => setPage(p + 1)}>Next</Btn></span>
                    </div>
                </React.Fragment>
            )}
        </React.Fragment>
    )
}
